import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Badge,
  Tooltip,
  InputAdornment,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Bed as BedIcon,
  Person as PersonIcon,
  CheckCircle as AvailableIcon,
  Cancel as OccupiedIcon,
  Build as MaintenanceIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  LocalHotel as HotelIcon,
  Bathroom as BathroomIcon,
  AcUnit as AcIcon,
  Tv as TvIcon,
  Wifi as WifiIcon,
  Kitchen as KitchenIcon,
  CurrencyRupee as CurrencyIcon,
  CalendarToday as CalendarIcon,
  CheckCircle,
  Sync as RefreshIcon
} from '@mui/icons-material';
import { useAppContext } from '../contexts/AppContext';
import RoomManagementService, { Room, RoomPatient, CreateRoomData, RoomStats } from '../services/RoomManagementService';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

// Bed Occupancy Manager Component
interface BedOccupancyManagerProps {
  room: Room | null;
  onUpdate: (occupiedBeds: number, totalBeds: number) => void;
  isLoading: boolean;
}

const BedOccupancyManager: React.FC<BedOccupancyManagerProps> = ({ room, onUpdate, isLoading }) => {
  const [selectedOccupiedBeds, setSelectedOccupiedBeds] = useState(room?.occupancy?.current || 0);
  const totalBeds = room?.capacity || room?.occupancy?.total || 1;

  const handleBedClick = (bedNumber: number, isOccupied: boolean) => {
    if (isOccupied) {
      // Free this bed
      setSelectedOccupiedBeds(Math.max(0, selectedOccupiedBeds - 1));
    } else {
      // Occupy this bed
      setSelectedOccupiedBeds(Math.min(totalBeds, selectedOccupiedBeds + 1));
    }
  };

  const handleQuickSelect = (occupiedCount: number) => {
    setSelectedOccupiedBeds(Math.max(0, Math.min(totalBeds, occupiedCount)));
  };

  return (
    <Box>
      {/* Quick Selection Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant={selectedOccupiedBeds === 0 ? 'contained' : 'outlined'}
          onClick={() => handleQuickSelect(0)}
          color="success"
        >
          All Empty
        </Button>
        <Button
          size="small"
          variant={selectedOccupiedBeds === totalBeds ? 'contained' : 'outlined'}
          onClick={() => handleQuickSelect(totalBeds)}
          color="error"
        >
          All Occupied
        </Button>
        {totalBeds > 2 && (
          <Button
            size="small"
            variant={selectedOccupiedBeds === Math.floor(totalBeds / 2) ? 'contained' : 'outlined'}
            onClick={() => handleQuickSelect(Math.floor(totalBeds / 2))}
            color="warning"
          >
            Half Occupied
          </Button>
        )}
      </Box>

      {/* Bed Visual Representation */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
          Beds ({selectedOccupiedBeds}/{totalBeds} occupied):
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Array.from({ length: totalBeds }, (_, index) => {
            const bedNumber = index + 1;
            const isOccupied = index < selectedOccupiedBeds;
            return (
              <Box
                key={bedNumber}
                onClick={() => handleBedClick(bedNumber, isOccupied)}
                sx={{
                  width: 60,
                  height: 40,
                  border: '2px solid',
                  borderColor: isOccupied ? '#EF4444' : '#10B981',
                  backgroundColor: isOccupied ? '#FEE2E2' : '#ECFDF5',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: 2
                  }
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: isOccupied ? '#EF4444' : '#10B981' }}>
                  {bedNumber}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Status Summary */}
      <Box sx={{ mb: 2, p: 2, bgcolor: '#F7FAFC', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          Status Summary:
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedOccupiedBeds === 0 && 'All beds available - Room will be marked as Available'}
          {selectedOccupiedBeds === totalBeds && 'All beds occupied - Room will be marked as Occupied'}
          {selectedOccupiedBeds > 0 && selectedOccupiedBeds < totalBeds && 
            `${selectedOccupiedBeds} bed(s) occupied, ${totalBeds - selectedOccupiedBeds} available - Room will be marked as Partially Occupied`}
        </Typography>
      </Box>

      {/* Update Button */}
      <Button
        variant="contained"
        fullWidth
        onClick={() => onUpdate(selectedOccupiedBeds, totalBeds)}
        disabled={isLoading}
        sx={{
          backgroundColor: selectedOccupiedBeds === 0 ? '#10B981' : selectedOccupiedBeds === totalBeds ? '#EF4444' : '#F59E0B',
          '&:hover': {
            backgroundColor: selectedOccupiedBeds === 0 ? '#059669' : selectedOccupiedBeds === totalBeds ? '#DC2626' : '#D97706'
          }
        }}
      >
        {isLoading ? 'Updating...' : 'Update Bed Occupancy'}
      </Button>
    </Box>
  );
};

const Rooms: React.FC = () => {
  // Get notification function from context
  const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    console.log(`${type}: ${message}`);
    // You can integrate with your actual notification system here
    setSnackbar({ open: true, message, type });
  };
  
  // State
  const [rooms, setRooms] = useState<Room[]>([]);
  const [filteredRooms, setFilteredRooms] = useState<Room[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('All Rooms');
  const [selectedFloor, setSelectedFloor] = useState<string>('All');
  const [selectedWard, setSelectedWard] = useState<string>('All');
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeRoom, setStatusChangeRoom] = useState<Room | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteRoom, setDeleteRoom] = useState<Room | null>(null);
  const [showBedManagementModal, setShowBedManagementModal] = useState(false);
  const [bedManagementRoom, setBedManagementRoom] = useState<Room | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [roomStats, setRoomStats] = useState<RoomStats | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'info' as 'success' | 'error' | 'warning' | 'info' });

  // Room form state
  const [roomForm, setRoomForm] = useState<CreateRoomData>({
    roomNumber: '',
    type: 'General Ward' as Room['type'],
    floor: '' as any,
    amenities: [] as string[],
    rate: '' as any,
    capacity: '' as any,
    department: 'General'
  });

  // Edit room form state
  const [editRoomForm, setEditRoomForm] = useState<CreateRoomData>({
    roomNumber: '',
    type: 'General Ward' as Room['type'],
    floor: '' as any,
    amenities: [] as string[],
    rate: '' as any,
    capacity: '' as any,
    department: 'General'
  });

  // Real-time Firebase data will be loaded in useEffect

  const floors = ['All', '1st Floor', '2nd Floor', '3rd Floor'];
  const wardTypes = ['All', 'General Ward', 'Private Room', 'ICU', 'VIP'];
  const statusOptions = ['All Rooms', 'Available', 'Occupied', 'Under Maintenance'];
  const roomStatusOptions = ['Available', 'Occupied', 'Partially Occupied', 'Under Maintenance', 'Out of Order'];
  const amenityOptions = ['Bathroom', 'Wardrobe', 'Oxygen', 'AC', 'TV', 'WiFi', 'Refrigerator', 'Monitor', 'Ventilator', 'Emergency Call', 'Sofa'];

  // Effects
  useEffect(() => {
    // Load initial room data and set up real-time listener
    console.log('🏥 Setting up room management...');
    setIsLoading(true);

    // Set up real-time listener for rooms
    const unsubscribeRooms = RoomManagementService.subscribeToRooms((firebaseRooms) => {
      console.log('📊 Received rooms update:', firebaseRooms.length);
      setRooms(firebaseRooms);
      setFilteredRooms(firebaseRooms);
      
      // Calculate statistics
      const stats = RoomManagementService.calculateRoomStats(firebaseRooms);
      setRoomStats(stats);
      
      setIsLoading(false);
    });

    // Set up real-time listener for patient changes (discharge/deletion)
    // This ensures rooms auto-update when patients are discharged or deleted
    const unsubscribePatients = onSnapshot(
      collection(db, 'patients'),
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const patientData = change.doc.data();
          
          // Check if patient was discharged or deleted
          if (change.type === 'removed' || 
              (change.type === 'modified' && patientData.statusText === 'Discharged')) {
            
            console.log(`👤 Patient ${change.type === 'removed' ? 'deleted' : 'discharged'}: ${patientData.name}`);
            
            // Trigger room status sync if patient had a room assignment
            if (patientData.room && patientData.bedNo) {
              console.log(`🔄 Auto-syncing room ${patientData.room} status...`);
              
              // Update room availability
              RoomManagementService.updateRoomAvailabilityForPatient(
                change.doc.id,
                patientData.room,
                patientData.bedNo
              ).then((result) => {
                if (result.success) {
                  console.log(`✅ ${result.message}`);
                } else {
                  console.warn(`⚠️ ${result.message}`);
                }
              }).catch((error) => {
                console.error('❌ Error auto-updating room:', error);
              });
            }
          }
        });
      },
      (error) => {
        console.error('❌ Error listening to patient changes:', error);
      }
    );

    // Fallback: load initial data if real-time fails
    const loadInitialData = async () => {
      try {
        const firebaseRooms = await RoomManagementService.getRooms();
        if (firebaseRooms.length > 0) {
          setRooms(firebaseRooms);
          setFilteredRooms(firebaseRooms);
          
          const stats = RoomManagementService.calculateRoomStats(firebaseRooms);
          setRoomStats(stats);
        }
      } catch (error) {
        console.error('❌ Error loading initial room data:', error);
        showNotification('Failed to load room data', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    // Load initial data as backup
    loadInitialData();

    // Cleanup subscriptions on unmount
    return () => {
      if (unsubscribeRooms) {
        unsubscribeRooms();
      }
      if (unsubscribePatients) {
        unsubscribePatients();
      }
    };
  }, []);

  useEffect(() => {
    filterRooms();
  }, [searchTerm, selectedStatus, selectedFloor, selectedWard, rooms]);

  // Functions
  const filterRooms = () => {
    let filtered = rooms;

    if (searchTerm) {
      filtered = filtered.filter(room => 
        room.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        room.roomNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        room.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        room.patients?.some(patient => 
          patient.name.toLowerCase().includes(searchTerm.toLowerCase())
        ) ||
        (room.patientName && room.patientName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedStatus !== 'All Rooms') {
      filtered = filtered.filter(room => room.status === selectedStatus);
    }

    if (selectedFloor !== 'All') {
      const floorNumber = parseInt(selectedFloor.split(' ')[0]);
      filtered = filtered.filter(room => room.floor === floorNumber);
    }

    if (selectedWard !== 'All') {
      filtered = filtered.filter(room => room.type === selectedWard);
    }

    setFilteredRooms(filtered);
  };

  const getStatusIcon = (status: Room['status']) => {
    switch (status) {
      case 'Available': return <AvailableIcon sx={{ color: '#10B981', fontSize: 20 }} />;
      case 'Occupied': return <OccupiedIcon sx={{ color: '#EF4444', fontSize: 20 }} />;
      case 'Under Maintenance': return <MaintenanceIcon sx={{ color: '#F59E0B', fontSize: 20 }} />;
    }
  };

  const getStatusColor = (status: Room['status']) => {
    switch (status) {
      case 'Available': return '#10B981';
      case 'Occupied': return '#EF4444';
      case 'Partially Occupied': return '#F59E0B';
      case 'Under Maintenance': return '#F59E0B';
      case 'Out of Order': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getStatusChipColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Available': return 'success';
      case 'Occupied': return 'error';
      case 'Partially Occupied': return 'warning';
      case 'Under Maintenance': return 'warning';
      case 'Out of Order': return 'error';
      default: return 'default';
    }
  };

  const getStatusColorHex = (status: string) => {
    switch (status) {
      case 'Available': return '#10B981';
      case 'Occupied': return '#EF4444';
      case 'Partially Occupied': return '#F59E0B';
      case 'Under Maintenance': return '#F59E0B';
      case 'Out of Order': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getAmenityIcon = (amenity: string) => {
    switch (amenity.toLowerCase()) {
      case 'bathroom': return <BathroomIcon sx={{ fontSize: 16 }} />;
      case 'ac': return <AcIcon sx={{ fontSize: 16 }} />;
      case 'tv': return <TvIcon sx={{ fontSize: 16 }} />;
      case 'wifi': return <WifiIcon sx={{ fontSize: 16 }} />;
      case 'wardrobe': return <HotelIcon sx={{ fontSize: 16 }} />;
      case 'refrigerator': 
      case 'kitchen': return <KitchenIcon sx={{ fontSize: 16 }} />;
      case 'oxygen': return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'monitor': return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'ventilator': return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'emergency call': return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'sofa': return <CheckCircle sx={{ fontSize: 16 }} />;
      default: return <CheckCircle sx={{ fontSize: 16 }} />;
    }
  };

  const handleViewRoom = (room: Room) => {
    setSelectedRoom(room);
    setShowViewModal(true);
  };

  const handleEditRoom = (room: Room) => {
    setEditingRoom(room);
    
    // Pre-populate the edit form with existing room data
    setEditRoomForm({
      roomNumber: room.roomNumber || room.number,
      type: room.type,
      floor: room.floor,
      amenities: room.amenities || [],
      rate: room.rate,
      capacity: room.capacity || room.totalBeds || 1,
      department: room.department || 'General'
    });
    
    setShowEditModal(true);
  };

  const handleDeleteRoom = (room: Room) => {
    setDeleteRoom(room);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRoom) return;

    setIsLoading(true);
    
    try {
      const result = await RoomManagementService.deleteRoom(deleteRoom.id);
      
      if (result.success) {
        setShowDeleteDialog(false);
        setDeleteRoom(null);
        showNotification('Room deleted successfully!', 'success');
      } else {
        showNotification(result.error || 'Failed to delete room', 'error');
      }
    } catch (error: any) {
      console.error('Error deleting room:', error);
      showNotification(`Error deleting room: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (room: Room) => {
    setStatusChangeRoom(room);
    setShowStatusModal(true);
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!statusChangeRoom) return;

    // If selecting "Partially Occupied", open bed management modal
    if (newStatus === 'Partially Occupied') {
      setBedManagementRoom(statusChangeRoom);
      setShowBedManagementModal(true);
      setShowStatusModal(false);
      return;
    }

    setIsLoading(true);
    
    try {
      // Check if document exists first
      const roomExists = await RoomManagementService.getRoomById(statusChangeRoom.id);
      if (!roomExists) {
        showNotification('Room not found. Please refresh the page and try again.', 'error');
        setIsLoading(false);
        return;
      }

      const updateData = {
        status: newStatus as Room['status'],
        isOccupied: newStatus === 'Occupied',
        occupancy: {
          current: newStatus === 'Occupied' ? (statusChangeRoom.capacity || 1) : 0,
          total: statusChangeRoom.capacity || 1
        },
        updatedAt: new Date().toISOString()
      };

      const result = await RoomManagementService.updateRoom(statusChangeRoom.id, updateData);
      
      if (result.success) {
        setShowStatusModal(false);
        setStatusChangeRoom(null);
        showNotification('Room status updated successfully!', 'success');
      } else {
        showNotification(result.error || 'Failed to update room status', 'error');
      }
    } catch (error: any) {
      console.error('Error updating room status:', error);
      showNotification(`Error updating room status: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBedManagement = (room: Room) => {
    setBedManagementRoom(room);
    setShowBedManagementModal(true);
  };

  const handleUpdateBedOccupancy = async (occupiedBeds: number, totalBeds: number) => {
    if (!bedManagementRoom) return;

    setIsLoading(true);
    
    try {
      // Check if document exists first
      const roomExists = await RoomManagementService.getRoomById(bedManagementRoom.id);
      if (!roomExists) {
        showNotification('Room not found. Please refresh the page and try again.', 'error');
        setIsLoading(false);
        return;
      }

      let status: Room['status'] = 'Available';
      if (occupiedBeds === 0) {
        status = 'Available';
      } else if (occupiedBeds === totalBeds) {
        status = 'Occupied';
      } else {
        status = 'Partially Occupied';
      }

      // Generate bed labels if not already present
      const roomNumber = bedManagementRoom.roomNumber || bedManagementRoom.number;
      const bedLabels = bedManagementRoom.bedLabels || 
        Array.from({ length: totalBeds }, (_, i) => `${roomNumber}${String.fromCharCode(65 + i)}`);
      
      // Get existing bed structure or create new one
      let updatedBeds: Array<{
        bedNumber: string;
        isOccupied: boolean;
        patientId?: string;
        patientName?: string;
        occupiedAt?: string;
      }> = [];
      
      if (bedManagementRoom.availableBeds && Array.isArray(bedManagementRoom.availableBeds)) {
        // Preserve existing bed structure
        updatedBeds = bedManagementRoom.availableBeds.map((bed: any) => {
          if (typeof bed === 'object' && bed.bedNumber) {
            return bed;
          }
          return { bedNumber: bed, isOccupied: false };
        });
      } else {
        // Create new bed structure
        updatedBeds = bedLabels.map((bedNum: string) => ({
          bedNumber: bedNum,
          isOccupied: false
        }));
      }
      
      // Update occupancy based on occupiedBeds parameter
      // Mark first N beds as occupied, rest as available
      updatedBeds = updatedBeds.map((bed, index) => {
        if (index < occupiedBeds) {
          // Keep existing patient info if available, otherwise mark as occupied
          return {
            ...bed,
            isOccupied: true,
            patientName: bed.patientName || 'Occupied',
            patientId: bed.patientId || 'manual',
            occupiedAt: bed.occupiedAt || new Date().toISOString()
          };
        } else {
          // Mark as available
          return {
            bedNumber: bed.bedNumber,
            isOccupied: false
          };
        }
      });

      const updateData = {
        status: status,
        isOccupied: occupiedBeds > 0,
        capacity: totalBeds,
        totalBeds: totalBeds,
        bedLabels: bedLabels,
        availableBeds: updatedBeds,
        currentOccupancy: occupiedBeds,
        occupancy: {
          current: occupiedBeds,
          total: totalBeds
        },
        statusColor: status === 'Occupied' ? '#EF4444' : status === 'Partially Occupied' ? '#F59E0B' : '#10B981',
        // Clear legacy patient info if no beds occupied
        patientName: occupiedBeds === 0 ? undefined : bedManagementRoom.patientName,
        patientId: occupiedBeds === 0 ? undefined : bedManagementRoom.patientId,
        admissionDate: occupiedBeds === 0 ? undefined : bedManagementRoom.admissionDate,
        updatedAt: new Date().toISOString()
      };

      const result = await RoomManagementService.updateRoom(bedManagementRoom.id, updateData);
      
      if (result.success) {
        setShowBedManagementModal(false);
        setBedManagementRoom(null);
        showNotification('Bed occupancy updated successfully!', 'success');
      } else {
        showNotification(result.error || 'Failed to update bed occupancy', 'error');
      }
    } catch (error: any) {
      console.error('Error updating bed occupancy:', error);
      showNotification(`Error updating bed occupancy: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRoom = async () => {
    if (!roomForm.roomNumber) {
      showNotification('Please enter room number', 'warning');
      return;
    }

    setIsLoading(true);
    
    try {
      const result = await RoomManagementService.createRoom(roomForm);
      
      if (result.success) {
        // Room will be automatically added via real-time listener
        setRoomForm({ 
          roomNumber: '', 
          type: 'General Ward', 
          floor: '' as any, 
          amenities: [], 
          rate: '' as any, 
          capacity: '' as any, 
          department: 'General' 
        });
        setShowAddModal(false);
        showNotification('Room added successfully!', 'success');
      } else {
        showNotification(result.error || 'Failed to add room', 'error');
      }
    } catch (error: any) {
      console.error('Error adding room:', error);
      showNotification(`Error adding room: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateRoom = async () => {
    if (!editRoomForm.roomNumber || !editingRoom) {
      showNotification('Please fill in all required fields', 'warning');
      return;
    }

    setIsLoading(true);
    
    try {
      // Prepare update data
      const updateData = {
        roomNumber: editRoomForm.roomNumber,
        type: editRoomForm.type,
        roomType: editRoomForm.type,
        floor: typeof editRoomForm.floor === 'string' ? parseInt(editRoomForm.floor) : editRoomForm.floor,
        amenities: editRoomForm.amenities,
        rate: typeof editRoomForm.rate === 'string' ? parseInt(editRoomForm.rate) : editRoomForm.rate,
        capacity: typeof editRoomForm.capacity === 'string' ? parseInt(editRoomForm.capacity) : editRoomForm.capacity,
        totalBeds: typeof editRoomForm.capacity === 'string' ? parseInt(editRoomForm.capacity) : editRoomForm.capacity,
        department: editRoomForm.department
      };

      const result = await RoomManagementService.updateRoom(editingRoom.id, updateData);
      
      if (result.success) {
        // Room will be automatically updated via real-time listener
        setShowEditModal(false);
        setEditingRoom(null);
        showNotification('Room updated successfully!', 'success');
      } else {
        showNotification(result.error || 'Failed to update room', 'error');
      }
    } catch (error: any) {
      console.error('Error updating room:', error);
      showNotification(`Error updating room: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDischargePatient = async (patientId: string, roomId: string) => {
    try {
      setIsLoading(true);
      
      const result = await RoomManagementService.dischargePatient(roomId);
      
      if (result.success) {
        // Room status will be updated via real-time listener
        showNotification('Patient discharged successfully!', 'success');
        // Close the view modal if it's open
        setShowViewModal(false);
      } else {
        showNotification(result.error || 'Failed to discharge patient', 'error');
      }
    } catch (error: any) {
      console.error('Error discharging patient:', error);
      showNotification(`Error discharging patient: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncRoomStatuses = async () => {
    try {
      setIsLoading(true);
      showNotification('Synchronizing room statuses with patient data...', 'info');
      
      const result = await RoomManagementService.syncRoomStatuses();
      
      if (result.success) {
        showNotification(
          `${result.message}. ${result.updatedRooms === 0 ? 'All rooms are already in sync!' : ''}`,
          'success'
        );
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error: any) {
      console.error('Error syncing room statuses:', error);
      showNotification(`Error syncing room statuses: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateRoomStatus = async (roomId: string, status: Room['status']) => {
    try {
      setIsLoading(true);
      
      const result = await RoomManagementService.updateRoomStatus(roomId, status);
      
      if (result.success) {
        // Room status will be updated via real-time listener
        showNotification(`Room status updated to ${status}`, 'success');
      } else {
        showNotification(result.error || 'Failed to update room status', 'error');
      }
    } catch (error: any) {
      console.error('Error updating room status:', error);
      showNotification(`Error updating room status: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Statistics from service calculations
  const totalRooms = roomStats?.totalRooms || 0;
  const occupiedRooms = roomStats?.occupiedRooms || 0;
  const availableRooms = roomStats?.availableRooms || 0;
  const maintenanceRooms = roomStats?.maintenanceRooms || 0;
  const occupancyRate = roomStats?.occupancyRate || 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1A202C', mb: 1 }}>
          Room Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096' }}>
          Monitor room occupancy and patient assignments
        </Typography>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search rooms, patients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Status Filters */}
        <Typography variant="h6" sx={{ mb: 2, color: '#4A5568' }}>Room Status</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {statusOptions.map((status) => (
            <Chip
              key={status}
              label={status}
              onClick={() => setSelectedStatus(status)}
              variant={selectedStatus === status ? 'filled' : 'outlined'}
              color={selectedStatus === status ? 'primary' : 'default'}
              icon={status !== 'All Rooms' ? getStatusIcon(status as Room['status']) : undefined}
            />
          ))}
        </Box>

        {/* Floor and Ward Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>Floor</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {floors.map((floor) => (
                <Chip
                  key={floor}
                  label={floor}
                  onClick={() => setSelectedFloor(floor)}
                  variant={selectedFloor === floor ? 'filled' : 'outlined'}
                  color={selectedFloor === floor ? 'primary' : 'default'}
                  size="small"
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>Ward Type</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {wardTypes.map((ward) => (
                <Chip
                  key={ward}
                  label={ward}
                  onClick={() => setSelectedWard(ward)}
                  variant={selectedWard === ward ? 'filled' : 'outlined'}
                  color={selectedWard === ward ? 'primary' : 'default'}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, 
        gap: 2, 
        mb: 3 
      }}>
        <Card sx={{ borderLeft: '4px solid #4A90E2' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4A90E2' }}>
                  {totalRooms}
                </Typography>
                <Typography variant="body2" sx={{ color: '#718096' }}>
                  Total Rooms
                </Typography>
              </Box>
              <BedIcon sx={{ fontSize: 40, color: '#4A90E2', opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderLeft: '4px solid #EF4444' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#EF4444' }}>
                  {occupiedRooms}
                </Typography>
                <Typography variant="body2" sx={{ color: '#718096' }}>
                  Occupied
                </Typography>
                <Typography variant="caption" sx={{ color: '#EF4444' }}>
                  {occupancyRate}% occupancy
                </Typography>
              </Box>
              <PersonIcon sx={{ fontSize: 40, color: '#EF4444', opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderLeft: '4px solid #10B981' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                  {availableRooms}
                </Typography>
                <Typography variant="body2" sx={{ color: '#718096' }}>
                  Available
                </Typography>
                <Typography variant="caption" sx={{ color: '#10B981' }}>
                  Ready for admission
                </Typography>
              </Box>
              <AvailableIcon sx={{ fontSize: 40, color: '#10B981', opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderLeft: '4px solid #F59E0B' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#F59E0B' }}>
                  {maintenanceRooms}
                </Typography>
                <Typography variant="body2" sx={{ color: '#718096' }}>
                  Maintenance
                </Typography>
                <Typography variant="caption" sx={{ color: '#F59E0B' }}>
                  Under repair
                </Typography>
              </Box>
              <MaintenanceIcon sx={{ fontSize: 40, color: '#F59E0B', opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Room List */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Rooms ({filteredRooms.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleSyncRoomStatuses}
              sx={{ borderRadius: 2 }}
              disabled={isLoading}
            >
              Sync Room Status
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddModal(true)}
              sx={{ borderRadius: 2 }}
              disabled={isLoading}
            >
              Add Room
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          {isLoading && rooms.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2, color: '#718096' }}>
                Loading rooms from database...
              </Typography>
            </Box>
          ) : filteredRooms.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" sx={{ color: '#718096' }}>
                {rooms.length === 0 ? 'No rooms found in database' : 'No rooms found matching your criteria'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }, 
              gap: 2 
            }}>
              {filteredRooms.map((room) => (
                <Card key={room.id} sx={{ border: '1px solid #E2E8F0', position: 'relative' }}>
                  <CardContent>
                    {/* Room Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ 
                          bgcolor: getStatusColor(room.status), 
                          width: 40, 
                          height: 40,
                          fontSize: '1.2rem',
                          fontWeight: 'bold'
                        }}>
                          {room.roomNumber || room.number}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {room.type}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#718096' }}>
                            Floor {room.floor} • {room.occupancy?.current || 0}/{room.occupancy?.total || room.capacity || 1} beds
                          </Typography>
                        </Box>
                      </Box>
                      <Chip 
                        label={room.status}
                        sx={{ 
                          backgroundColor: getStatusColor(room.status),
                          color: 'white',
                          fontWeight: 'medium'
                        }}
                        size="small"
                      />
                    </Box>

                    {/* Occupancy Info */}
                    {room.occupancy && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <BedIcon sx={{ fontSize: 16, color: '#718096' }} />
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            Occupied Beds ({room.occupancy.current}/{room.occupancy.total})
                          </Typography>
                        </Box>

                        {room.patients && Array.isArray(room.patients) && room.patients.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            {room.patients.map((patient, index) => {
                              // Ensure patient is a valid object with required properties
                              if (!patient || typeof patient !== 'object' || !patient.id) {
                                console.warn('Invalid patient data:', patient);
                                return null;
                              }
                              
                              return (
                                <Box key={patient.id} sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  p: 1,
                                  bgcolor: '#F7FAFC',
                                  borderRadius: 1,
                                  mb: index < room.patients!.length - 1 ? 1 : 0
                                }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {String(patient.bedNumber || 'N/A')} {String(patient.name || 'Unknown')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#718096' }}>
                                      ID: {String(patient.patientId || 'N/A')}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#718096' }}>
                                    {typeof patient.admissionDate === 'string' ? patient.admissionDate : (patient.admissionDate ? String(patient.admissionDate) : 'N/A')}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Amenities */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                        {(room.amenities || []).length} amenities
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(room.amenities || []).slice(0, 4).map((amenity) => (
                          <Chip
                            key={amenity}
                            label={amenity}
                            size="small"
                            variant="outlined"
                            icon={getAmenityIcon(amenity)}
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                        {(room.amenities || []).length > 4 && (
                          <Chip
                            label={`+${(room.amenities || []).length - 4} more`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Rate */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CurrencyIcon sx={{ fontSize: 16, color: '#718096' }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          ₹{room.rate.toLocaleString()}/day
                        </Typography>
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewRoom(room)}
                        sx={{ flex: 1, minWidth: '100px' }}
                      >
                        View
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditRoom(room)}
                        sx={{ color: '#F59E0B', borderColor: '#F59E0B', flex: 1, minWidth: '80px' }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleStatusChange(room)}
                        sx={{ color: '#10B981', borderColor: '#10B981', flex: 1, minWidth: '80px' }}
                      >
                        Status
                      </Button>
                      {(room.capacity || 1) > 1 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleBedManagement(room)}
                          sx={{ color: '#8B5CF6', borderColor: '#8B5CF6', flex: 1, minWidth: '80px' }}
                        >
                          Beds
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleDeleteRoom(room)}
                        sx={{ color: '#EF4444', borderColor: '#EF4444', flex: 1, minWidth: '80px' }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      {/* View Room Modal */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Room Details</DialogTitle>
        <DialogContent>
          {selectedRoom && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
                gap: 3 
              }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Room Information</Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Room Number:</strong> {selectedRoom.roomNumber || selectedRoom.number}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Type:</strong> {selectedRoom.type}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Floor:</strong> {selectedRoom.floor}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Status:</strong> {selectedRoom.status}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Rate:</strong> ₹{selectedRoom.rate.toLocaleString()}/day
                  </Typography>
                  {selectedRoom.lastCleaned && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Last Cleaned:</strong> {selectedRoom.lastCleaned}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Occupancy Details</Typography>
                  {selectedRoom.occupancy ? (
                    <>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Beds:</strong> {selectedRoom.occupancy.current}/{selectedRoom.occupancy.total} occupied
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        <strong>Occupancy Rate:</strong> {Math.round((selectedRoom.occupancy.current / selectedRoom.occupancy.total) * 100)}%
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ mb: 2, color: '#718096' }}>
                      No occupancy data available
                    </Typography>
                  )}
                </Box>
              </Box>

              {selectedRoom.patients && Array.isArray(selectedRoom.patients) && selectedRoom.patients.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Current Patients ({selectedRoom.patients.length})</Typography>
                  <List>
                    {selectedRoom.patients.map((patient) => {
                      // Ensure patient is a valid object with required properties
                      if (!patient || typeof patient !== 'object' || !patient.id) {
                        console.warn('Invalid patient data in modal:', patient);
                        return null;
                      }
                      
                      return (
                        <ListItem key={patient.id} sx={{ border: '1px solid #E2E8F0', mb: 1, borderRadius: 1 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: '#4A90E2' }}>
                              {String(patient.name || 'U').charAt(0)}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={`${String(patient.bedNumber || 'N/A')} - ${String(patient.name || 'Unknown')}`}
                            secondary={
                              <Box>
                                <Typography variant="body2">
                                  ID: {String(patient.patientId || 'N/A')}
                                </Typography>
                                <Typography variant="body2">
                                  Admitted: {typeof patient.admissionDate === 'string' ? patient.admissionDate : (patient.admissionDate ? String(patient.admissionDate) : 'N/A')}
                                </Typography>
                                {patient.condition && (
                                  <Typography variant="body2" sx={{ color: patient.condition === 'Critical' ? '#EF4444' : '#718096' }}>
                                    Condition: {String(patient.condition)}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{ color: '#EF4444', borderColor: '#EF4444' }}
                            onClick={() => {
                              handleDischargePatient(patient.id, selectedRoom.id);
                              setShowViewModal(false);
                            }}
                          >
                            Discharge
                          </Button>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Amenities</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(selectedRoom.amenities || []).map((amenity) => (
                    <Chip
                      key={amenity}
                      label={amenity}
                      icon={getAmenityIcon(amenity)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Room Modal */}
      <Dialog
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Room</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Room Number *"
              value={roomForm.roomNumber}
              onChange={(e) => setRoomForm({ ...roomForm, roomNumber: e.target.value })}
            />
            
            <TextField
              fullWidth
              select
              label="Room Type *"
              value={roomForm.type}
              onChange={(e) => setRoomForm({ ...roomForm, type: e.target.value as Room['type'] })}
              SelectProps={{ native: true }}
            >
              <option value="General Ward">General Ward</option>
              <option value="Private Room">Private Room</option>
              <option value="ICU">ICU</option>
              <option value="VIP">VIP</option>
            </TextField>

            <TextField
              fullWidth
              type="number"
              label="Floor *"
              value={roomForm.floor}
              onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value === '' ? '' : parseInt(e.target.value) })}
            />

            <TextField
              fullWidth
              type="number"
              label="Rate per Day (₹) *"
              value={roomForm.rate}
              onChange={(e) => setRoomForm({ ...roomForm, rate: e.target.value === '' ? '' : parseInt(e.target.value) })}
              inputProps={{ min: 0 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Bed Capacity *"
              value={roomForm.capacity}
              onChange={(e) => setRoomForm({ ...roomForm, capacity: e.target.value === '' ? '' : parseInt(e.target.value) })}
              inputProps={{ min: 1, max: 10 }}
            />

            <TextField
              fullWidth
              label="Department"
              value={roomForm.department}
              onChange={(e) => setRoomForm({ ...roomForm, department: e.target.value })}
              placeholder="e.g., General Medicine, Cardiology, ICU"
            />

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                Amenities
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {amenityOptions.map((amenity) => (
                  <Chip
                    key={amenity}
                    label={amenity}
                    onClick={() => {
                      const newAmenities = roomForm.amenities.includes(amenity)
                        ? roomForm.amenities.filter(a => a !== amenity)
                        : [...roomForm.amenities, amenity];
                      setRoomForm({ ...roomForm, amenities: newAmenities });
                    }}
                    variant={roomForm.amenities.includes(amenity) ? 'filled' : 'outlined'}
                    color={roomForm.amenities.includes(amenity) ? 'primary' : 'default'}
                    size="small"
                    icon={getAmenityIcon(amenity)}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button 
            onClick={handleAddRoom} 
            variant="contained"
            disabled={!roomForm.roomNumber || !roomForm.rate || isLoading}
          >
            {isLoading ? 'Adding...' : 'Add Room'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Room Modal */}
      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div">
            Edit Room
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Room Number"
              value={editRoomForm.roomNumber}
              onChange={(e) => setEditRoomForm({ ...editRoomForm, roomNumber: e.target.value })}
              variant="outlined"
              required
            />

            <TextField
              fullWidth
              label="Room Type"
              select
              value={editRoomForm.type}
              onChange={(e) => setEditRoomForm({ ...editRoomForm, type: e.target.value as Room['type'] })}
              variant="outlined"
              SelectProps={{ native: true }}
            >
              <option value="General Ward">General Ward</option>
              <option value="Private Room">Private Room</option>
              <option value="ICU">ICU</option>
              <option value="VIP">VIP</option>
            </TextField>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Floor"
                type="number"
                value={editRoomForm.floor}
                onChange={(e) => setEditRoomForm({ ...editRoomForm, floor: Number(e.target.value) })}
                variant="outlined"
                inputProps={{ min: 1 }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Capacity"
                type="number"
                value={editRoomForm.capacity}
                onChange={(e) => setEditRoomForm({ ...editRoomForm, capacity: Number(e.target.value) })}
                variant="outlined"
                inputProps={{ min: 1 }}
                sx={{ flex: 1 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Rate per Day (₹)"
                type="number"
                value={editRoomForm.rate}
                onChange={(e) => setEditRoomForm({ ...editRoomForm, rate: Number(e.target.value) })}
                variant="outlined"
                inputProps={{ min: 0 }}
                required
                sx={{ flex: 1 }}
              />
              <TextField
                label="Department"
                value={editRoomForm.department}
                onChange={(e) => setEditRoomForm({ ...editRoomForm, department: e.target.value })}
                variant="outlined"
                sx={{ flex: 1 }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                Amenities
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {amenityOptions.map((amenity) => (
                  <Chip
                    key={amenity}
                    label={amenity}
                    onClick={() => {
                      const newAmenities = editRoomForm.amenities.includes(amenity)
                        ? editRoomForm.amenities.filter(a => a !== amenity)
                        : [...editRoomForm.amenities, amenity];
                      setEditRoomForm({ ...editRoomForm, amenities: newAmenities });
                    }}
                    variant={editRoomForm.amenities.includes(amenity) ? 'filled' : 'outlined'}
                    color={editRoomForm.amenities.includes(amenity) ? 'primary' : 'default'}
                    size="small"
                    icon={getAmenityIcon(amenity)}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateRoom} 
            variant="contained"
            disabled={!editRoomForm.roomNumber || !editRoomForm.rate || isLoading}
            sx={{ 
              backgroundColor: '#F59E0B',
              '&:hover': { backgroundColor: '#D97706' }
            }}
          >
            {isLoading ? 'Updating...' : 'Update Room'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Modal */}
      <Dialog open={showStatusModal} onClose={() => setShowStatusModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div">
            Change Room Status
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Room {statusChangeRoom?.roomNumber || statusChangeRoom?.number} - {statusChangeRoom?.type}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Current Status: <Chip 
                label={String(statusChangeRoom?.status || 'Available')} 
                color={getStatusChipColor(String(statusChangeRoom?.status || 'Available'))} 
                size="small" 
              />
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
              Select New Status:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {roomStatusOptions.map((status) => (
                <Button
                  key={status}
                  variant={statusChangeRoom?.status === status ? 'contained' : 'outlined'}
                  onClick={() => handleUpdateStatus(status)}
                  disabled={statusChangeRoom?.status === status || isLoading}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    color: statusChangeRoom?.status === status ? 'white' : getStatusColorHex(status),
                    borderColor: getStatusColorHex(status),
                    backgroundColor: statusChangeRoom?.status === status ? getStatusColorHex(status) : 'transparent',
                    '&:hover': {
                      backgroundColor: statusChangeRoom?.status === status ? getStatusColorHex(status) : `${getStatusColorHex(status)}10`
                    }
                  }}
                >
                  {status}
                </Button>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStatusModal(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Bed Management Modal */}
      <Dialog open={showBedManagementModal} onClose={() => setShowBedManagementModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div">
            Manage Bed Occupancy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Room {bedManagementRoom?.roomNumber || bedManagementRoom?.number} - {bedManagementRoom?.type}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Current Status: <Chip 
                  label={String(bedManagementRoom?.status || 'Available')} 
                  color={getStatusChipColor(String(bedManagementRoom?.status || 'Available'))} 
                  size="small" 
                />
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total Beds: {bedManagementRoom?.capacity || bedManagementRoom?.occupancy?.total || 1}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Currently Occupied: {bedManagementRoom?.occupancy?.current || 0}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 'medium' }}>
                Update Bed Occupancy:
              </Typography>
              <BedOccupancyManager 
                room={bedManagementRoom}
                onUpdate={handleUpdateBedOccupancy}
                isLoading={isLoading}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBedManagementModal(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="sm">
        <DialogTitle>
          <Typography variant="h6" component="div" sx={{ color: '#EF4444' }}>
            Delete Room
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete room <strong>{deleteRoom?.roomNumber || deleteRoom?.number}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Room Type: {deleteRoom?.type}
          </Typography>
          <Typography variant="body2" color="error.main">
            ⚠️ This action cannot be undone. All room data and patient assignments will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete Room'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Rooms;